import { forwardRef } from 'react';
import { createGenericContext, createBemBlockBuilder } from '@ebsco-ui/utils';
import { jsx, jsxs } from 'react/jsx-runtime';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var _createGenericContext = createGenericContext({
  errorMessage: "Listbox compound components cannot be rendered outside of the Listbox component"
}),
    useListboxContext = _createGenericContext.useGenericContext;
    _createGenericContext.GenericContextConsumer;
    var ListboxContextProvider = _createGenericContext.GenericContextProvider;

var ListboxLabel = function ListboxLabel(_ref) {
  var children = _ref.children;

  var _useListboxContext = useListboxContext(),
      getBemBlocksWith = _useListboxContext.getBemBlocksWith;

  return /*#__PURE__*/jsx("div", {
    className: getBemBlocksWith("__label"),
    "data-auto": "listbox-label",
    children: children
  });
};

var keyCodes = {
  32: "SPACE_KEY",
  38: "UP_ARROW",
  40: "DOWN_ARROW",
  9: "TAB_KEY"
};
var keys = {
  DOWN_ARROW: "DOWN_ARROW",
  UP_ARROW: "UP_ARROW",
  SPACE_KEY: "SPACE_KEY",
  TAB_KEY: "TAB_KEY"
};

var isListContainer = function isListContainer(_id, currentElement) {
  var _currentElement$getAt;

  return currentElement === null || currentElement === void 0 ? void 0 : (_currentElement$getAt = currentElement.getAttribute("id")) === null || _currentElement$getAt === void 0 ? void 0 : _currentElement$getAt.includes("listbox-list");
}; // To focus the item


var focusItem = function focusItem(item) {
  return item === null || item === void 0 ? void 0 : item.focus();
};

var activedescendant = "aria-activedescendant"; //To handle the keydown events for Listbox

var handleKeyDown = function handleKeyDown(event, id, item, isItemDisabled, selectOrUnselectItem) {
  var _previousItem$parentE;

  event.stopPropagation();
  var key = keyCodes[event === null || event === void 0 ? void 0 : event.keyCode];
  var currentElement = event === null || event === void 0 ? void 0 : event.currentTarget; // eslint-disable-next-line default-case

  switch (key) {
    case keys.DOWN_ARROW:
      var shouldFocusFirstItem = isListContainer(id, currentElement); //If the current element is a list container then focus the first item else focus the next item

      if (shouldFocusFirstItem) {
        var _firstItem$parentElem;

        var firstItem = currentElement === null || currentElement === void 0 ? void 0 : currentElement.firstChild;
        focusItem(firstItem);
        /*Setting "aria-activedescendant" property to parent element 
        because inorder to notify the current focused child element for the screen readers.*/

        return firstItem === null || firstItem === void 0 ? void 0 : (_firstItem$parentElem = firstItem.parentElement) === null || _firstItem$parentElem === void 0 ? void 0 : _firstItem$parentElem.setAttribute(activedescendant, firstItem === null || firstItem === void 0 ? void 0 : firstItem.getAttribute("id"));
      } else {
        var _nextItem$parentEleme;

        var nextItem = currentElement === null || currentElement === void 0 ? void 0 : currentElement.nextSibling;
        focusItem(nextItem);
        return nextItem === null || nextItem === void 0 ? void 0 : (_nextItem$parentEleme = nextItem.parentElement) === null || _nextItem$parentEleme === void 0 ? void 0 : _nextItem$parentEleme.setAttribute(activedescendant, nextItem === null || nextItem === void 0 ? void 0 : nextItem.getAttribute("id"));
      }

    case keys.UP_ARROW:
      //To focus the previous item
      var previousItem = currentElement === null || currentElement === void 0 ? void 0 : currentElement.previousSibling;
      focusItem(previousItem);
      return previousItem === null || previousItem === void 0 ? void 0 : (_previousItem$parentE = previousItem.parentElement) === null || _previousItem$parentE === void 0 ? void 0 : _previousItem$parentE.setAttribute(activedescendant, previousItem === null || previousItem === void 0 ? void 0 : previousItem.getAttribute("id"));

    case keys.SPACE_KEY:
      // If the list item is already selected then unselect it and vice versa..
      if (item && !isItemDisabled && selectOrUnselectItem) {
        selectOrUnselectItem(item);
      }

      event.preventDefault();
      break;

    case keys.TAB_KEY:
      var parentElement = currentElement === null || currentElement === void 0 ? void 0 : currentElement.parentElement; // To notify the screen reader that there is no active element inside the listbox

      return isListContainer(id, parentElement) && parentElement.setAttribute(activedescendant, "");
  }
};

var ListboxList = function ListboxList(_ref) {
  var children = _ref.children;

  var _useListboxContext = useListboxContext(),
      getBemBlocksWith = _useListboxContext.getBemBlocksWith,
      _useListboxContext$id = _useListboxContext.id,
      id = _useListboxContext$id === void 0 ? "" : _useListboxContext$id;

  return /*#__PURE__*/jsx("div", {
    className: getBemBlocksWith("__list"),
    "data-auto": id,
    id: "listbox-list-".concat(id),
    tabIndex: 0,
    "aria-multiselectable": true,
    role: "listbox",
    "aria-label": "listbox-list-".concat(id),
    onKeyDown: function onKeyDown(event) {
      handleKeyDown(event, id);
    },
    children: children
  });
};

var ListboxListItem = function ListboxListItem(_ref) {
  var item = _ref.item,
      disabled = _ref.disabled,
      handleSelection = _ref.handleSelection;

  var _useListboxContext = useListboxContext(),
      allowTwoColumns = _useListboxContext.allowTwoColumns,
      firstColumnKey = _useListboxContext.firstColumnKey,
      secondColumnKey = _useListboxContext.secondColumnKey,
      getBemBlocksWith = _useListboxContext.getBemBlocksWith;

  return /*#__PURE__*/jsx("div", {
    id: item[firstColumnKey],
    className: cn(getBemBlocksWith("__list-item"), "".concat(disabled ? getBemBlocksWith("__list-item__disabled") : "")),
    tabIndex: -1,
    onClick: function onClick() {
      return handleSelection(item);
    },
    role: "option",
    "aria-selected": item.isSelected,
    "aria-disabled": disabled,
    onKeyDown: function onKeyDown(event) {
      handleKeyDown(event, item[firstColumnKey], item, disabled, handleSelection);
    },
    children: /*#__PURE__*/jsxs("div", {
      className: getBemBlocksWith("__checkbox-row"),
      children: [/*#__PURE__*/jsx("div", {
        className: getBemBlocksWith("__list-item__icon"),
        children: item.isSelected && /*#__PURE__*/jsx(FontAwesomeIcon, {
          icon: faCheck,
          size: "1x"
        })
      }), /*#__PURE__*/jsxs("div", {
        className: getBemBlocksWith("__list-item__column__wrapper"),
        "data-auto": "listbox-listitem-column-wrapper",
        children: [firstColumnKey && /*#__PURE__*/jsx("div", {
          className: getBemBlocksWith("__list-item__first-column"),
          "data-auto": "listbox-listitem-first-column",
          children: item[firstColumnKey]
        }), allowTwoColumns && secondColumnKey && /*#__PURE__*/jsx("div", {
          className: getBemBlocksWith("__list-item__second-column"),
          "data-auto": "listbox-listitem-second-column",
          children: item[secondColumnKey]
        })]
      })]
    })
  });
};

var ListboxEmpty = function ListboxEmpty(_ref) {
  var children = _ref.children;

  var _useListboxContext = useListboxContext(),
      getBemBlocksWith = _useListboxContext.getBemBlocksWith;

  return /*#__PURE__*/jsx("div", {
    tabIndex: 0,
    className: getBemBlocksWith("__empty"),
    "data-auto": "listbox-empty",
    children: children
  });
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var Listbox = forwardRef(function (_ref, ref) {
  var _ref$allowTwoColumns = _ref.allowTwoColumns,
      allowTwoColumns = _ref$allowTwoColumns === void 0 ? false : _ref$allowTwoColumns,
      children = _ref.children,
      firstColumnKey = _ref.firstColumnKey,
      _ref$secondColumnKey = _ref.secondColumnKey,
      secondColumnKey = _ref$secondColumnKey === void 0 ? "" : _ref$secondColumnKey,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? "" : _ref$className,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? "" : _ref$id;
  var getBemBlocksWith = createBemBlockBuilder(["eb-listbox", className]);
  var listboxProps = {
    allowTwoColumns: allowTwoColumns,
    children: children,
    firstColumnKey: firstColumnKey,
    secondColumnKey: secondColumnKey,
    id: id
  };

  var contextProviderValue = _objectSpread(_objectSpread({}, listboxProps), {}, {
    getBemBlocksWith: getBemBlocksWith
  });

  return /*#__PURE__*/jsx(ListboxContextProvider, {
    value: contextProviderValue,
    children: /*#__PURE__*/jsx("div", {
      className: getBemBlocksWith(),
      "data-auto": "listbox",
      ref: ref,
      children: children
    })
  });
});
Listbox.Label = ListboxLabel;
Listbox.List = ListboxList;
Listbox.ListItem = ListboxListItem;
Listbox.Empty = ListboxEmpty;
Listbox.handleKeyDown = handleKeyDown;
Listbox.displayName = "Listbox";

export { Listbox as default };
//# sourceMappingURL=listbox-esm.js.map
